import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import type { StatusBannerVariant } from '@metro/station-card'

/**
 * G-10 line-status analytics (LOCKED cross-platform contract, catalog §G-10
 * — iOS = canon; exact snake_case names + enum values byte-identical on iOS /
 * Android / Web). This host module owns the `line_status_viewed` emit for the
 * station-card surface (the web line-status-badge analogue, the `StatusBanner`
 * on `StationCardView`).
 *
 *   line_status_viewed { line_id, severity, surface }
 *
 * The two `disruption_detail_*` events live inside `@metro/suggester`
 * (which owns the Disruption Details phase state machine + already emits
 * `suggester_disruption_open` through its `onAnalytics` sink); see
 * `Suggester.tsx`.
 *
 * **Web surface scope.** Web emits `line_status_viewed` with `surface:
 * 'station'` only — the `StatusBanner` on the station card is the one web
 * surface that renders a line-status badge with a resolvable `line_id` (the
 * selected station's line). The `map` and `suggester` surface values are part
 * of the contract for iOS / Android (which render line-status badges on those
 * surfaces); web's suggester rows show a per-STATION disruption composite
 * already covered by `suggester_disruption_open`, and the web map renders no
 * line-status badge. When a web map/suggester line-status badge lands, emit
 * the matching `surface` value here.
 *
 * **Severity mapping (decouple-at-emit, same pattern as night-mode §1.8).**
 * The `StatusBanner` display variant (`closed | restricted | advisory`) maps
 * onto the contract's 5-value severity set
 * (`good_service | minor | severe | closure | advisory`):
 *   closed     → closure   (the station/line is closed)
 *   restricted → advisory  (limited service = a non-blocking advisory)
 *   advisory   → advisory
 * Web never renders `good_service | minor | severe` (it has no line-level
 * service buckets yet); those remain reserved for iOS / Android.
 */

/** `line_status_viewed.severity` — the locked 5-value contract set. */
export type LineStatusSeverity = 'good_service' | 'minor' | 'severe' | 'closure' | 'advisory'

/** `line_status_viewed.surface` — the locked 3-value contract set. */
export type LineStatusSurface = 'map' | 'station' | 'suggester'

const EVENT_LINE_STATUS_VIEWED = 'line_status_viewed'

/**
 * Map the web `StatusBanner` display variant onto the contract severity wire
 * value. `restricted` (limited service) is an advisory-level disruption, not a
 * full closure — it shares the `advisory` bucket.
 */
export function lineStatusSeverityFromVariant(variant: StatusBannerVariant): LineStatusSeverity {
  switch (variant) {
    case 'closed':
      return 'closure'
    case 'restricted':
      return 'advisory'
    case 'advisory':
      return 'advisory'
  }
}

/**
 * Emit `line_status_viewed { line_id, severity, surface }`. `line_id` is the
 * public line-schema id (e.g. `line-victoria`), never a naptanId. Caller is
 * responsible for the once-per-appearance de-dupe (see
 * `useLineStatusViewedAnalytics`).
 */
export function reportLineStatusViewed(
  reporter: MetroAnalyticsReporter,
  params: { lineId: string; severity: LineStatusSeverity; surface: LineStatusSurface },
): void {
  reporter(EVENT_LINE_STATUS_VIEWED, {
    line_id: params.lineId,
    severity: params.severity,
    surface: params.surface,
  })
}
